import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container } from "react-bootstrap";
import { Button } from "../../components/ui";
import GetData from "../../services/getData";
import logPatientChange from "../../services/patientLogs";
import { buildPatientLogText } from "../../services/patientLogMessages";

/**
 * Страница перехода по ID клиента YClients.
 *
 * Если пациент с таким id_yc уже есть — редирект на его карточку.
 * Если нет — карточка создаётся автоматически (ФИО берётся из записи
 * расписания, иначе подтягивается из YClients, иначе — служебная подпись)
 * и пользователь сразу попадает на созданного пациента.
 * Диалога «Создать пациента?» больше нет.
 */
export default function PatientYc() {
    const { t } = useTranslation("patient");
    const { idYc } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const fioFromState = location.state?.fio || "";
    const clinicIdFromState = location.state?.clinic_id || "";

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Защита от повторного создания при двойном срабатывании эффекта (StrictMode).
    const creatingRef = useRef(false);

    const createPatient = useCallback(async () => {
        if (creatingRef.current) return;
        creatingRef.current = true;
        try {
            const res = await GetData({
                module: "patient",
                block: "resolve_yc",
                action: "create",
                id_yc: String(idYc).trim(),
                fio: String(fioFromState || "").trim() || undefined,
                clinic_id: clinicIdFromState || undefined,
            });
            if (res && res.success && res.id != null) {
                await logPatientChange({
                    idPatient: res.id,
                    text: buildPatientLogText("patient_created_from_yc", {
                        details: `ID YClients: ${String(idYc).trim()}; ФИО: ${
                            res.fio || fioFromState || "—"
                        }`,
                    }),
                });
                navigate(`/patient/${res.id}/overview`, { replace: true });
                return;
            }
            setError(res?.message || t("yc.createFailed"));
        } catch (err) {
            setError(t("yc.requestError"));
            console.error(err);
        } finally {
            creatingRef.current = false;
            setLoading(false);
        }
    }, [idYc, fioFromState, clinicIdFromState, navigate, t]);

    const resolvePatient = useCallback(async () => {
        if (!idYc) {
            setError(t("yc.noId"));
            setLoading(false);
            return;
        }
        setError(null);
        try {
            const res = await GetData({
                module: "patient",
                block: "resolve_yc",
                action: "get",
                id_yc: String(idYc).trim(),
            });
            if (res && res.success && res.id != null) {
                navigate(`/patient/${res.id}/overview`, { replace: true });
                return;
            }
            if (res && res.success && res.not_found) {
                // Пациента нет — создаём сразу, без вопроса пользователю.
                await createPatient();
                return;
            }
            setError(res?.message || t("yc.resolveFailed"));
            setLoading(false);
        } catch (err) {
            setError(t("yc.requestError"));
            console.error(err);
            setLoading(false);
        }
    }, [idYc, navigate, createPatient, t]);

    useEffect(() => {
        resolvePatient();
    }, [resolvePatient]);

    if (error) {
        return (
            <Container fluid="md" className="py-5">
                <div className="text-center text-danger">{error}</div>
                <div className="text-center mt-2">
                    <Button variant="secondary" onClick={() => navigate("/schedule/")}>
                        {t("yc.backToSchedule")}
                    </Button>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid="md" className="py-5">
            <div className="text-center">
                {loading ? t("yc.creating") : t("yc.loading")}
            </div>
        </Container>
    );
}
